// src/utils/theme.ts
import { useSyncExternalStore } from "react";

/**
 * Tema claro/oscuro.
 *
 * Antes cada botón (navbar y pantalla de acceso) tocaba `document.body` por su
 * cuenta y guardaba el estado en su propio `useState`: la preferencia se perdía
 * al recargar y los dos iconos podían quedar desincronizados entre sí.
 *
 * Acá vive la única fuente de verdad: se persiste en localStorage, se aplica al
 * `<body>` (los estilos del calendario dependen de `body.dark`) y se propaga a
 * cualquier componente suscripto — incluyendo otras pestañas abiertas.
 */
export type Theme = "light" | "dark";

const STORAGE_KEY = "theme";

let listeners: Array<() => void> = [];
let current: Theme = "light";

const canUseDOM = typeof document !== "undefined";

function readStored(): Theme | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "dark" || stored === "light" ? stored : null;
  } catch {
    // Modo privado o storage bloqueado: seguimos con el valor en memoria.
    return null;
  }
}

function paint(theme: Theme) {
  if (!canUseDOM || !document.body) return;
  document.body.classList.toggle("dark", theme === "dark");
}

function emit() {
  listeners.forEach((listener) => listener());
}

export function getTheme(): Theme {
  return current;
}

export function setTheme(theme: Theme) {
  if (theme === current) return;
  current = theme;
  paint(theme);
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* sin persistencia, pero la sesión actual sigue funcionando */
  }
  emit();
}

export function toggleTheme() {
  setTheme(current === "dark" ? "light" : "dark");
}

export function subscribe(listener: () => void) {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

/**
 * Se llama una vez al arrancar la app. El script inline de index.html ya pintó
 * la clase para evitar el parpadeo; esto sincroniza el estado en memoria y deja
 * escuchando los cambios hechos desde otra pestaña.
 */
export function initTheme() {
  current = readStored() ?? "light";
  paint(current);

  if (!canUseDOM) return;
  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY) return;
    const next = readStored() ?? "light";
    if (next === current) return;
    current = next;
    paint(next);
    emit();
  });
}

/** Hook de lectura: cualquier control del tema muestra siempre el estado real. */
export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getTheme, getTheme);
  return { theme, isDark: theme === "dark", setTheme, toggleTheme };
}
